import { useCallback, useMemo } from "react"

import { AttributionDateRange, ChannelExpense, ExpenseEditingRules } from "./types"
import { getMonthKeyForTimezone, getMonthKeysInRange, isSingleMonthRange, monthKeyToUtcRange } from "@/lib/date-utils"

interface UseExpenseManagementOptions {
  expenses: ChannelExpense[]
  dateRange: AttributionDateRange
}

interface ExpensePermissionResult {
  canEdit: boolean
  reason: string
}

interface DeletePermissionResult {
  canDelete: boolean
  reason: string
}

export const useExpenseManagement = ({
  expenses,
  dateRange,
}: UseExpenseManagementOptions) => {
  const expenseEditingRules: ExpenseEditingRules = useMemo(() => {
    const isSingleMonth = isSingleMonthRange(
      dateRange.month_start_utc,
      dateRange.month_end_utc,
      dateRange.timezone,
    )
    const monthKeys = getMonthKeysInRange(
      dateRange.month_start_utc,
      dateRange.month_end_utc,
      dateRange.timezone,
    )
    const isMultiMonth = monthKeys.length > 1
    const currentMonthKey = isSingleMonth
      ? getMonthKeyForTimezone(dateRange.month_start_utc, dateRange.timezone)
      : monthKeys[0] ?? getMonthKeyForTimezone(new Date(), dateRange.timezone)

    const expensesByMonth: Record<string, ChannelExpense[]> = {}
    expenses.forEach((expense) => {
      const monthKey =
        expense.period_month && expense.period_month.trim().length === 7
          ? expense.period_month
          : getMonthKeyForTimezone(new Date(expense.period_start), dateRange.timezone)
      if (!expensesByMonth[monthKey]) {
        expensesByMonth[monthKey] = []
      }
      expensesByMonth[monthKey].push(expense)
    })

    return {
      isEditable: isSingleMonth,
      isMultiMonth,
      currentMonthKey,
      monthKeys,
      expensesByMonth,
    }
  }, [expenses, dateRange])

  const canEditChannelExpense = useCallback(
    (channelSlug: string, opts?: { allowChannelLevelExpense?: boolean }): ExpensePermissionResult => {
      const { isEditable, isMultiMonth, currentMonthKey, expensesByMonth } = expenseEditingRules

      if (opts?.allowChannelLevelExpense === false) {
        return { canEdit: false, reason: "Channel-level expenses are disabled for this channel" }
      }

      if (isMultiMonth) {
        return { canEdit: false, reason: "Expense is read-only when viewing multiple months" }
      }

      if (!isEditable) {
        return { canEdit: false, reason: "Expense can only be set within a single calendar month" }
      }

      const currentMonthExpenses = expensesByMonth[currentMonthKey] || []
      const hasSourceExpense = currentMonthExpenses.some((e) => e.channel_slug === channelSlug && e.source_name)

      if (hasSourceExpense) {
        return { canEdit: false, reason: "Channel expense is disabled because sources have expenses for this month" }
      }

      return { canEdit: true, reason: "" }
    },
    [expenseEditingRules],
  )

  const canEditSourceExpense = useCallback(
    (channelSlug: string, _source: string): ExpensePermissionResult => {
      const { isEditable, isMultiMonth, currentMonthKey, expensesByMonth } = expenseEditingRules

      if (isMultiMonth) {
        return { canEdit: false, reason: "Expense is read-only when viewing multiple months" }
      }

      if (!isEditable) {
        return { canEdit: false, reason: "Expense can only be set within a single calendar month" }
      }

      const currentMonthExpenses = expensesByMonth[currentMonthKey] || []
      const hasChannelExpense = currentMonthExpenses.some((e) => e.channel_slug === channelSlug && !e.source_name)

      if (hasChannelExpense) {
        return { canEdit: false, reason: "Source expense is disabled because channel has an expense for this month" }
      }

      return { canEdit: true, reason: "" }
    },
    [expenseEditingRules],
  )

  const getExpenseId = useCallback(
    (channelSlug: string, source: string | undefined, monthKey: string): string | null => {
      const { expensesByMonth } = expenseEditingRules
      const monthExpenses = expensesByMonth[monthKey] || []

      const expense = source
        ? monthExpenses.find((e) => e.channel_slug === channelSlug && e.source_name === source)
        : monthExpenses.find((e) => e.channel_slug === channelSlug && !e.source_name)

      return expense?.id || null
    },
    [expenseEditingRules],
  )

  const shouldShowDelete = useCallback(
    (channelSlug: string, source: string | undefined): boolean => {
      const { currentMonthKey, isMultiMonth } = expenseEditingRules

      if (isMultiMonth) return false

      const expenseId = getExpenseId(channelSlug, source, currentMonthKey)
      return expenseId !== null
    },
    [expenseEditingRules, getExpenseId],
  )

  const canDeleteExpense = useCallback(
    (channelSlug: string, source: string | undefined): DeletePermissionResult => {
      const { isEditable, isMultiMonth } = expenseEditingRules

      if (isMultiMonth) {
        return { canDelete: false, reason: "Delete is available only for a single month." }
      }

      if (!isEditable) {
        return { canDelete: false, reason: "Delete is available only for a single month." }
      }

      if (source) {
        const sourceEditRules = canEditSourceExpense(channelSlug, source)
        if (!sourceEditRules.canEdit) {
          return { canDelete: false, reason: sourceEditRules.reason }
        }
      } else {
        const channelEditRules = canEditChannelExpense(channelSlug)
        if (!channelEditRules.canEdit) {
          return { canDelete: false, reason: channelEditRules.reason }
        }
      }

      return { canDelete: true, reason: "" }
    },
    [canEditChannelExpense, canEditSourceExpense, expenseEditingRules],
  )

  const getEffectiveChannelExpense = useCallback(
    (channelSlug: string): number => {
      const { isMultiMonth, monthKeys, expensesByMonth, currentMonthKey } = expenseEditingRules

      if (isMultiMonth) {
        let total = 0
        monthKeys.forEach((monthKey) => {
          const monthExpenses = expensesByMonth[monthKey] || []
          const channelExpense = monthExpenses.find((e) => e.channel_slug === channelSlug && !e.source_name)
          const sourceExpenses = monthExpenses.filter((e) => e.channel_slug === channelSlug && e.source_name)

          if (channelExpense) {
            const amount =
              typeof channelExpense.expense_amount === "number"
                ? channelExpense.expense_amount
                : Number(channelExpense.expense_amount ?? 0)
            total += amount
          }
          if (sourceExpenses.length > 0) {
            total += sourceExpenses.reduce((sum, e) => {
              const amount = typeof e.expense_amount === "number" ? e.expense_amount : Number(e.expense_amount ?? 0)
              return sum + amount
            }, 0)
          }
        })
        return total
      }

      const currentMonthExpenses = expensesByMonth[currentMonthKey] || []
      const channelExpense = currentMonthExpenses.find((e) => e.channel_slug === channelSlug && !e.source_name)
      const sourceExpenses = currentMonthExpenses.filter((e) => e.channel_slug === channelSlug && e.source_name)

      let total = 0
      if (channelExpense) {
        const amount =
          typeof channelExpense.expense_amount === "number"
            ? channelExpense.expense_amount
            : Number(channelExpense.expense_amount ?? 0)
        total += amount
      }
      if (sourceExpenses.length > 0) {
        total += sourceExpenses.reduce((sum, e) => {
          const amount = typeof e.expense_amount === "number" ? e.expense_amount : Number(e.expense_amount ?? 0)
          return sum + amount
        }, 0)
      }
      return total
    },
    [expenseEditingRules],
  )

  const getEffectiveSourceExpense = useCallback(
    (channelSlug: string, source: string): number => {
      const { isMultiMonth, monthKeys, expensesByMonth, currentMonthKey } = expenseEditingRules

      if (isMultiMonth) {
        let total = 0
        monthKeys.forEach((monthKey) => {
          const monthExpenses = expensesByMonth[monthKey] || []
          const sourceExpense = monthExpenses.find((e) => e.channel_slug === channelSlug && e.source_name === source)
          if (sourceExpense) {
            const amount =
              typeof sourceExpense.expense_amount === "number"
                ? sourceExpense.expense_amount
                : Number(sourceExpense.expense_amount ?? 0)
            total += amount
          }
        })
        return total
      }

      const currentMonthExpenses = expensesByMonth[currentMonthKey] || []
      const sourceExpense = currentMonthExpenses.find((e) => e.channel_slug === channelSlug && e.source_name === source)
      if (!sourceExpense) return 0
      return typeof sourceExpense.expense_amount === "number"
        ? sourceExpense.expense_amount
        : Number(sourceExpense.expense_amount ?? 0)
    },
    [expenseEditingRules],
  )

  const getExpensePeriodForMonth = useCallback(
    (monthKey: string) => {
      const { month_start_utc, month_end_utc } = monthKeyToUtcRange(monthKey, dateRange.timezone)
      return {
        month_start_utc: month_start_utc.toISOString(),
        month_end_utc: month_end_utc.toISOString(),
        month_key: monthKey,
      }
    },
    [dateRange.timezone],
  )

  return {
    expenseEditingRules,
    canEditChannelExpense,
    canEditSourceExpense,
    canDeleteExpense,
    shouldShowDelete,
    getExpenseId,
    getEffectiveChannelExpense,
    getEffectiveSourceExpense,
    getExpensePeriodForMonth,
  }
}
